import {WdiModalAppProps} from "@/components/WdiControls/index";
import {ResponseCode} from "@/utils/response";
import {BlockOutlined, CloseOutlined} from "@ant-design/icons";
import {Col, Form, Input, Row, Spin} from "antd";
import Table, {ColumnsType} from "antd/lib/table";
import React, {useEffect, useRef, useState } from 'react';
import {WdiModal, WdiModalProps} from "./WdiModalForm";
import {WdiSelectFormat, WdiTreeSelect, WdiTreeSelectProps} from "@/components/WdiControls/WdiSelect";

type SelectParseModalProps = {
  selectsRemoteSearch: (selectValues: Set<any>,optionMap:Map<any,WdiTreeSelectComponentStates> ) => Promise<WdiTreeSelectComponentStates[]>;
  inputsRemoteSearch: (inputKeys: Set<any>,optionMap:Map<any,WdiTreeSelectComponentStates>) => Promise<WdiTreeSelectComponentStates[]>;
  disabled?: boolean;
  selectValues?: Set<any>;
  ref?: any;
} & WdiModalAppProps;


type WdiTreeParseSelectProps = {
  selectsRemoteSearch: (selectValues: Set<any>,optionMap:Map<any,WdiTreeSelectComponentStates> ) => Promise<WdiTreeSelectComponentStates[]>;
  inputsRemoteSearch: (inputKeys: Set<any>,optionMap:Map<any,WdiTreeSelectComponentStates>) => Promise<WdiTreeSelectComponentStates[]>;
  onChange?: (dataSource: any[]) => void;
} & WdiTreeSelectProps;

export type WdiTreeSelectComponentStates = {
  // remote 模式下value不能赋值需要使用label: value模式
  value: any,
  label: any,
  level: number,
  isRed?: boolean,
};

export const WdiTreeParseSelect: React.FC<WdiTreeParseSelectProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const [selectValues, setSelectValues] = useState<Set<any>>(new Set());
  const treeSelectRef: any = useRef()
  const selectModalRef: any = useRef()

  useEffect(() => {
    if (!props.value) {
      setSelectValues(new Set())
      return
    }
    setSelectValues(new Set(
      [].concat(props.value).map(item => WdiSelectFormat.getvalue(item))
    ))
  }, [props.value]);


  const showParseModal = () => {
    if (props.disabled) {
      return;
    }
    setModalProps({visible: true, title: '复制粘贴'});
  };

  return (
    <>
      {modalProps.visible && (
        <SelectParseModalApp
          ref={selectModalRef}
          disabled={props.disabled}
          onClose={() => setModalProps({visible: false})}
          onSave={(result) => {
            const options:WdiTreeSelectComponentStates[] = result?.data ?? [];
            const userSelectValues = options.map(item=>item.value);
            setModalProps({visible: false});
            //修改后的值和修改前的值一致不去触发change事件
            if(userSelectValues.join() == Array.from(selectValues).join()){
              return;
            }
            setSelectValues(new Set(userSelectValues))
            const optionState = selectModalRef.current?.getOptionState();
            if(optionState?.delOptionMap.size){
              //保留label和value，防止树下拉框后续没加载，但是实际有值
              props.onChange?.(options)
            }
            if(optionState?.addOptionMap.size){
              //有新增模拟exSelect
              const dict = treeSelectRef.current?.getDict();
              optionState.addOptionMap.values().filter((option: any) => !option?.isRed).forEach((option: any) => props.onExSelect?.(option.value, option, dict))
            }
          }}
          selectsRemoteSearch={props.selectsRemoteSearch}
          inputsRemoteSearch={props.inputsRemoteSearch}
          visible={modalProps.visible}
          selectValues={selectValues}
        />
      )}
      <WdiTreeSelect
        {...props}
        ref={treeSelectRef}
        style={{width: '85%'}}
      />
      {!props.disabled && (
        <BlockOutlined
          onClick={() => showParseModal()}
          style={{fontSize: 20, position: 'absolute', top: 5}}
        />
      )}
    </>
  );
};

const getDefaultOptionState = ()=>{
  return {version: 0, defaultOptionMap: new Map(), addOptionMap: new Map, delOptionMap: new Map(), optionMap: new Map()}
}


export const SelectParseModalApp: React.FC<SelectParseModalProps> = React.forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  //记录每次打开已选择 新增 删除的 option
  const [optionsState, setOptionsState] = useState<{
    version: number,
    defaultOptionMap: Map<any, WdiTreeSelectComponentStates>,
    addOptionMap: Map<any, WdiTreeSelectComponentStates>,
    delOptionMap: Map<any, WdiTreeSelectComponentStates>,
    //最终结果
    optionMap: Map<any, WdiTreeSelectComponentStates>,
  }>(getDefaultOptionState());


  //导出ref 获取内部数据
  React.useImperativeHandle(ref, () => ({
    getOptionState: () => {
      return optionsState;
    },
  }));


  useEffect(() => {
    if (!props.selectValues?.size) {
      setOptionsState(getDefaultOptionState());
      return
    }
    //初始化用户点击选择的
    props.selectsRemoteSearch(props.selectValues,optionsState.optionMap).then(selectDataSource => {
      selectDataSource.forEach((item) => {
        optionsState.optionMap.set(item.label, item)
        optionsState.defaultOptionMap.set(item.label, item)
      })
      setOptionsState({...optionsState, version: optionsState.version + 1})
    })
  }, [props.selectValues]);

  const columns: ColumnsType<any> = [
    {
      title: props.disabled ? undefined : (
        <CloseOutlined
          onClick={() => {
            if (window.confirm('确定要删除所有行吗？')) {
              optionsState.delOptionMap = optionsState.defaultOptionMap;
              optionsState.optionMap = new Map();
              setOptionsState({...optionsState, version: optionsState.version + 1});
            }
          }}
        />
      ),
      align: 'center',
      width: 50,
      dataIndex: 'delete',
      ellipsis: true,
      render: (_, record) => props.disabled ? null : (
        <CloseOutlined
          onClick={() => {
            optionsState.optionMap.delete(record.label)
            optionsState.delOptionMap.set(record.label, record)
            setOptionsState({...optionsState, version: optionsState.version + 1})
          }}
        />
      ),
    },
    {
      title: '内容',
      dataIndex: 'label',
      ellipsis: true,
      render: (value, record) => <span style={record.isRed ? {color: 'red'} : {}}>{value}</span>,
    },
  ];

  const buildDataSource = async (value: string) => {
    if (!value) return
    const result = value
      .replaceAll(/\r\n/g, ',')
      .replaceAll(/\n/g, ',')
      .replaceAll('，', ',')
      .replaceAll(';', ',')
      .replaceAll('；', ',');
    const inputKeySet = new Set(result.split(',').filter((v) => v != ''));
    const newDataSource =  await props.inputsRemoteSearch?.(inputKeySet,optionsState.optionMap)
    newDataSource.forEach((item) => {
      const defaultIsExists = optionsState.defaultOptionMap.get(item.label);
      const delIsExists = optionsState.delOptionMap.get(item.label);
      if(!defaultIsExists){
        optionsState.addOptionMap.set(item.label, item)
      }
      if(delIsExists){
        optionsState.delOptionMap.delete(item.label)
      }
      optionsState.optionMap.set(item.label, item)
    })
    setOptionsState({...optionsState,version: optionsState.version + 1});
    form.setFieldsValue({
      content: '',
    });
  };

  const handelParse = (e: any) => {
    setLoading(true);
    setTimeout(() => {
      let value = e.target.value
      buildDataSource(value).then(res => {
        setLoading(false);
      });
    }, 100);
  };

  const handelEnter = (e: any) => {
    if ('enter' == e?.key.toLocaleLowerCase()) {
      if ('textarea' == e.target?.tagName.toLocaleLowerCase()) {
        setLoading(true);
        setTimeout(() => {
          let value = e.target.value;
          buildDataSource(value).then(res => {
            setLoading(false);
          });
        }, 100);
      }
    }
  };

  return (
    <WdiModal
      title={'复制粘贴'}
      visible={props.visible}
      width={800}
      onCancel={() => {
        props.onClose?.();
      }}
      onOk={() => {
        if (props.disabled) {
          return;
        }
        const legalOptionMap = new Map();
        optionsState.optionMap.forEach((val, key) => {
          if (val?.isRed || !val?.value) {
            return;
          }
          legalOptionMap.set(key, val)
        })
        setOptionsState({...optionsState, version: optionsState.version + 1, optionMap: legalOptionMap})
        props.onSave?.({
          code: ResponseCode.SUCCESS,
          data: Array.from(legalOptionMap.values()),
        });
      }}
    >
      <Spin tip="识别中，请稍候" spinning={loading}>
        <Form form={form}>
          <Row>
            <Col span={24}>
              <Form.Item name="content" label="粘贴至此">
                <Input.TextArea disabled={props.disabled} onPaste={handelParse} onKeyUp={handelEnter}/>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          size="small"
          dataSource={Array.from(optionsState.optionMap.values())}
          pagination={false}
        />
      </Spin>
    </WdiModal>
  );
})
